import type { SelectOption } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useCallback, useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import type { DropdownFilter } from '@/types/graphql/enums'
import type { SelectProps as BaseSelectProps } from './Select'
import { Select } from './Select'
import type {
  AllIssueRiskAspectsQuery,
  AllIssueRiskAspectsQueryVariables,
  IssueRiskAspectNode,
} from '@/modules/issues'
import { ALL_ISSUE_RISK_ASPECTS_QUERY } from '@/modules/issues'

interface IssueRiskAspectSelectSelectProps
  extends Omit<BaseSelectProps<string, IssueRiskAspectNode>, 'options'> {
  alwaysInclude?: IssueRiskAspectNode
  dropdownPageName?: DropdownFilter
  isDiet?: boolean
  showInManualTimeEntryOvertime?: boolean
  dataset?: IssueRiskAspectNode[]
}

export const IssueRiskAspectSelect: React.FC<
  IssueRiskAspectSelectSelectProps
> = ({
  alwaysInclude = null,
  dropdownPageName,
  nullable = true,

  // TODO unused?
  isDiet: _isDiet = false,
  showInManualTimeEntryOvertime: _showInManualTimeEntryOvertime = false,

  ...selectProps
}) => {
  const { issueRiskAspect: placeholder } = useTranslate({
    issueRiskAspect: 'common.risk-aspect',
  })

  const [query, setQuery] = useState('')
  const { data, loading } = useQuery<
    AllIssueRiskAspectsQuery,
    AllIssueRiskAspectsQueryVariables
  >(ALL_ISSUE_RISK_ASPECTS_QUERY)

  const getLabel = useCallback((riskAspect: IssueRiskAspectNode) => {
    return <span className="name">{riskAspect.name}</span>
  }, [])

  const options = useMemo<SelectOption<string, IssueRiskAspectNode>[]>(() => {
    if (!data) return []

    const options = data.allIssueRiskAspects.edges
      .filter(edge =>
        edge.node.name.toLowerCase().includes(query.toLowerCase())
      )
      .map(({ node: issueRiskAspect }) => ({
        value: issueRiskAspect.id,
        label: getLabel(issueRiskAspect),
        extra: issueRiskAspect,
      }))
    if (alwaysInclude === null) return options

    return [
      {
        value: alwaysInclude.id,
        label: getLabel(alwaysInclude),
        extra: alwaysInclude,
      },
      ...options.filter(option => option.extra.id !== alwaysInclude.id),
    ]
  }, [alwaysInclude, data, getLabel, query])

  return (
    <Select
      options={options}
      loading={loading}
      placeholder={placeholder}
      searchable={false}
      filterBackground="white"
      optionHoverBackground="white"
      onFilterChange={setQuery}
      nullable={nullable}
      {...selectProps}
    />
  )
}
